// src/lib/agentApi.ts
import { DimensionScores, ReadinessBand, Recommendation } from './quizState';
import { Dimension, DIMENSION_LABELS, DIMENSION_ORDER } from '../data/quizQuestions';

export interface AgentReadInput {
  band: ReadinessBand;
  recommendation: Recommendation;
  scores: DimensionScores;
  email?: string;
  answers?: Array<{ question: string; answer: string }>;
}

export interface AgentRead {
  verdict: string;
  observations: string[];
  recommendedMode: 'Plan' | 'Embed' | 'Hand-off' | 'Not yet' | 'Not a fit';
  agenda: string[];
  nextStepLabel: string;
  nextStepHref: string;
}

export interface CalendlyParams {
  base: string;
  name: string;
  email: string;
  agenda: string[];
}

export interface DiagnosticIntakePayload {
  name: string;
  phone: string;
  email: string;
  company?: string;
  scores: DimensionScores;
  band: ReadinessBand;
  recommendation: Recommendation;
  agenda: string[];
  answers?: Array<{ question: string; answer: string }>;
}

const AGENT_URL = 'https://agent.parsant.io/chat';
const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL || '';
const N8N_WEBHOOK_TOKEN = process.env.REACT_APP_WEBHOOK_TOKEN || '';
const AGENT_AUTH_HEADER = process.env.REACT_APP_AGENT_AUTH || '';

export async function getAgentRead(input: AgentReadInput): Promise<AgentRead> {
  try {
    const res = await fetch(AGENT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Webhook-Auth': AGENT_AUTH_HEADER,
      },
      body: JSON.stringify({
        type: 'diagnostic_read',
        score_vector: input.scores,
        readiness_band: input.band,
        recommendation: input.recommendation,
        answers: input.answers ?? [],
      }),
    });
    if (!res.ok) throw new Error(`Agent returned ${res.status}`);
    const data = await res.json();
    if (
      !data.verdict || !Array.isArray(data.observations) ||
      data.observations.length !== 3 || !data.recommendedMode ||
      !Array.isArray(data.agenda) || data.agenda.length < 3
    ) {
      throw new Error('Agent response shape invalid');
    }
    return {
      verdict: data.verdict,
      observations: data.observations,
      recommendedMode: data.recommendedMode,
      agenda: data.agenda.slice(0, 5),
      nextStepLabel: data.nextStepLabel ?? 'Schedule the call',
      nextStepHref: data.nextStepHref ?? '#schedule',
    };
  } catch (err) {
    console.warn('[agentApi] falling back to template:', err);
    return fallbackTemplate(input);
  }
}

export async function submitLeadCapture(input: AgentReadInput & { email: string }): Promise<void> {
  if (!N8N_WEBHOOK_URL) {
    console.warn('[agentApi] N8N webhook URL not configured; skipping lead capture');
    return;
  }
  try {
    await fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Webhook-Auth': N8N_WEBHOOK_TOKEN },
      body: JSON.stringify({
        source: 'parsant-diagnostic',
        email: input.email,
        readiness_band: input.band,
        recommendation: input.recommendation,
        score_vector: input.scores,
      }),
    });
  } catch (err) {
    console.warn('[agentApi] lead capture failed:', err);
  }
}

/**
 * Submit the full diagnostic intake + contact info to n8n with a flag
 * to trigger an immediate outbound call from the VAPI agent (Elliot).
 *
 * The n8n workflow on the receiving end is responsible for:
 *  1. Saving the lead to Twenty CRM (lifeDomain=Parsant)
 *  2. Triggering a VAPI outbound call to the supplied phone number
 *     with the diagnostic context attached to Elliot's prompt
 *  3. Logging the trigger + outcome
 *
 * Returns true only when the webhook acknowledged the intake, so the UI
 * never promises a call that was not actually triggered. Network failures
 * are logged and reported as false; the readiness report still renders regardless.
 */
export async function submitDiagnosticIntake(input: DiagnosticIntakePayload): Promise<boolean> {
  if (!N8N_WEBHOOK_URL) {
    console.warn('[agentApi] N8N webhook URL not configured; skipping intake + outbound trigger');
    return false;
  }
  try {
    const res = await fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Webhook-Auth': N8N_WEBHOOK_TOKEN },
      body: JSON.stringify({
        source: 'parsant-diagnostic',
        trigger_outbound_call: true,
        contact: {
          name: input.name,
          phone: input.phone,
          email: input.email,
          company: input.company ?? '',
        },
        readiness_band: input.band,
        recommendation: input.recommendation,
        score_vector: input.scores,
        agenda: input.agenda,
        answers: input.answers ?? [],
      }),
    });
    return res.ok;
  } catch (err) {
    console.warn('[agentApi] diagnostic intake failed:', err);
    return false;
  }
}

export function fallbackTemplate(input: AgentReadInput): AgentRead {
  const ranked = DIMENSION_ORDER
    .map(d => ({ dim: d, pct: input.scores[d] / DIMENSION_MAX[d] }))
    .sort((a, b) => a.pct - b.pct);

  const weakest = ranked.slice(0, 3);
  const observations = weakest.map(({ dim }) => observationFor(dim, input.scores[dim]));
  const agenda = buildAgenda(input.recommendation.mode, weakest.map(w => w.dim));

  let verdict = '';
  switch (input.band) {
    case 'Strong':
      verdict = 'Your AI readiness is strong across the board. Embed is the right shape. The foundation is ready and the work is to ship into production.';
      break;
    case 'Capable':
      verdict = 'Your engagement fit is capable, with a few dimensions worth strengthening alongside the build. Plan or Embed both fit, depending on velocity.';
      break;
    case 'Mixed':
      verdict = 'The profile is mixed. A Plan engagement will pressure-test which dimensions need attention before committing to a larger build.';
      break;
    case 'Not Yet':
      verdict = 'The foundation is not yet ready for this work. Fixing the weakest dimensions first protects the investment.';
      break;
  }

  return {
    verdict,
    observations,
    recommendedMode: input.recommendation.mode,
    agenda,
    nextStepLabel: input.recommendation.mode === 'Not a fit' ? 'Read the FAQ' :
                   input.recommendation.mode === 'Not yet' ? 'See what to fix first' :
                   'Schedule the call',
    nextStepHref: input.recommendation.mode === 'Not a fit' ? '/faq' :
                  input.recommendation.mode === 'Not yet' ? '/why' :
                  '#schedule',
  };
}

export function calendlyUrl({ base, name, email, agenda }: CalendlyParams): string {
  if (!base) return '';
  const agendaText = agenda.map((a, i) => `${i + 1}. ${a}`).join('\n');
  // Use URLSearchParams for name + email (produces + for spaces, as Calendly expects).
  // Encode a1 with encodeURIComponent so decodeURIComponent correctly recovers the agenda text.
  const nameEmail = new URLSearchParams({ name, email }).toString();
  return `${base}?${nameEmail}&a1=${encodeURIComponent(agendaText)}`;
}

function buildAgenda(mode: AgentRead['recommendedMode'], weakDims: Dimension[]): string[] {
  if (mode === 'Not a fit') {
    return [
      'Why we don\'t think Parsant is the right shape here',
      'What would have to change for a Parsant engagement to make sense',
      'Who in our network might be a better fit',
    ];
  }
  if (mode === 'Not yet') {
    return [
      'The specific blockers in your foundation that need to be addressed first',
      'Practical sequencing to get the foundation ready',
      'When to come back to Parsant once you\'re past the blockers',
    ];
  }
  const base = mode === 'Plan'
    ? [
        'Your situation in your own words, and what changed that prompted this',
        'The workflow you\'d most want to put on Parsant\'s plate first',
        'How a Plan engagement would scope the next 90 days',
      ]
    : mode === 'Embed'
    ? [
        'Your situation in your own words, and what changed that prompted this',
        'The workflow you\'d most want to embed Parsant against',
        'How an Embed engagement would shape the next 3 to 6 months',
      ]
    : [
        'Your situation in your own words, and the current state of the system',
        'What "ready to hand off" looks like for your team',
        'Owner staffing and exit-criteria specifics',
      ];
  if (weakDims.length > 0) {
    base.push(`How we'd address your weakest dimension (${DIMENSION_LABELS[weakDims[0]]}) inside the engagement`);
  }
  return base;
}

const DIMENSION_MAX: Record<Dimension, number> = {
  systems: 6, regulatory: 6, sponsor: 6, technical: 6, scope: 6,
};

function observationFor(dim: Dimension, score: number): string {
  const label = DIMENSION_LABELS[dim];
  const max = DIMENSION_MAX[dim];
  const pct = Math.round((score / max) * 100);
  const seeds: Record<Dimension, string> = {
    systems: `${label} (${pct}% of max). A fragmented or absent system of record means AI outputs have nowhere reliable to land. Establish one authoritative source before embedding an operator.`,
    regulatory: `${label} (${pct}% of max). Compliance frameworks shape what AI can touch and how outputs must be audited. Maturity here is a prerequisite in regulated markets.`,
    sponsor: `${label} (${pct}% of max). Without a named executive sponsor and an identifiable trainable owner, this engagement cannot land or hand off cleanly. That owner must exist on day one.`,
    technical: `${label} (${pct}% of max). The team cannot yet operate the system. Scope the build to match operating capacity, not aspiration. One on-call engineer with runbooks is the minimum.`,
    scope: `${label} (${pct}% of max). Clarity on where you sit in the engagement lifecycle (Plan, Embed, or Hand-off) determines the shape of the work and the budget required.`,
  };
  return seeds[dim];
}
